import type { MedplumClient } from '@medplum/core';
import type { Bundle, Condition, Provenance } from '@medplum/fhirtypes';

import {
  type FhirConnectionState,
  type FhirQueryState,
  useFhirConnection,
  useFhirData,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import {
  CONDITION_VER_STATUS_SYSTEM,
  REVIEW_STATUS_ATTESTED,
  REVIEW_STATUS_REJECTED,
  isPendingReview,
  isRefuted,
  setReviewStatusTag,
} from '../review-markers';

import { FHIR_PATIENT_ID_QUERY, PATIENT_CONDITIONS_QUERY } from './fhirQueries';

/**
 * Data layer for the patient-profile Conditions card: reads via the FHIR
 * GraphQL connection, review writes via the Medplum SDK client. The two
 * paths share no cache — callers refetch the read after a write lands.
 */

function mapPatientId(
  data: ResultOf<typeof FHIR_PATIENT_ID_QUERY>,
): string | null {
  return data.PatientList?.[0]?.id ?? null;
}

/** Resolve the FHIR Patient id for an Eluve (Hasura) patient id. */
export function useFhirPatientId(
  patientId: string,
): FhirQueryState<string | null> {
  return useFhirData(FHIR_PATIENT_ID_QUERY, mapPatientId, {
    variables: { identifier: patientId },
  });
}

function mapConditions(
  data: ResultOf<typeof PATIENT_CONDITIONS_QUERY>,
): Condition[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is Condition & { id: string } =>
      resource.resourceType === 'Condition' && Boolean(resource.id),
  );
}

/** The patient's conditions, paged newest-first. Skipped until the FHIR id resolves. */
export function usePatientConditions(
  fhirPatientId: string | null,
): FhirConnectionState<Condition[]> {
  return useFhirConnection(
    PATIENT_CONDITIONS_QUERY,
    'ConditionConnection',
    mapConditions,
    {
      variables: { patientRef: `Patient/${fhirPatientId}` },
      skip: !fhirPatientId,
    },
  );
}

export type ReviewDecision =
  | { kind: 'accept'; editedDisplay?: string }
  | { kind: 'dismiss' };

function verification(code: string): Condition['verificationStatus'] {
  return { coding: [{ system: CONDITION_VER_STATUS_SYSTEM, code }] };
}

/**
 * Apply a reviewer's decision to a pending AI-drafted condition — the same
 * transitions the backend review capability performs
 * (`clinical-findings/finding-types/condition.ts`): re-read, verify still
 * pending, flip the verificationStatus + review-status tag, and record an
 * attestation Provenance, all in one transaction so the audit trail can't
 * drift from the decision.
 *
 * - accept → verificationStatus `confirmed`, tag `attested`. A `refuted`
 *   draft (a captured denial) stays `refuted` — approving attests the
 *   denial was correctly captured, never that the diagnosis is present.
 * - accept with an edited name → `code` replaced by the free-text display
 *   (the AI's coding is dropped, exactly like the backend edit path when no
 *   validated code is chosen), then confirmed as above.
 * - dismiss → verificationStatus `entered-in-error`, tag `rejected`.
 *
 * FHIR invariant con-5: `clinicalStatus` SHALL NOT be present when the
 * verificationStatus is entered-in-error or refuted — dropped on those
 * paths, or the PUT is rejected.
 */
export async function reviewCondition(
  client: MedplumClient,
  args: {
    conditionId: string;
    decision: ReviewDecision;
    /** `Practitioner/<id>` recorded as the verifier on the Provenance. */
    reviewerReference: string | null;
  },
): Promise<void> {
  const { conditionId, decision, reviewerReference } = args;
  const current = await client.readResource('Condition', conditionId);
  if (!isPendingReview(current)) {
    throw new Error('This condition is no longer awaiting review.');
  }

  let next: Condition;
  if (decision.kind === 'dismiss') {
    const { clinicalStatus: _drop, ...rest } = current;
    next = setReviewStatusTag(
      { ...rest, verificationStatus: verification('entered-in-error') },
      REVIEW_STATUS_REJECTED,
    );
  } else {
    const edited: Condition =
      decision.editedDisplay !== undefined
        ? { ...current, code: { text: decision.editedDisplay } }
        : current;
    if (isRefuted(edited)) {
      const { clinicalStatus: _drop, ...rest } = edited;
      next = setReviewStatusTag(
        { ...rest, verificationStatus: verification('refuted') },
        REVIEW_STATUS_ATTESTED,
      );
    } else {
      next = setReviewStatusTag(
        { ...edited, verificationStatus: verification('confirmed') },
        REVIEW_STATUS_ATTESTED,
      );
    }
  }

  const entries: NonNullable<Bundle['entry']> = [
    {
      request: { method: 'PUT', url: `Condition/${conditionId}` },
      resource: next,
    },
  ];
  if (decision.kind === 'accept' && reviewerReference) {
    entries.push({
      request: { method: 'POST', url: 'Provenance' },
      resource: buildAttestationProvenance(
        `Condition/${conditionId}`,
        reviewerReference,
      ),
    });
  }

  const response = await client.executeBatch({
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  });
  assertNoFailedEntries(response, entries.length);
}

const DATA_OPERATION_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/provenance-participant-type';

/** Attestation Provenance: the reviewer verified this finding. */
function buildAttestationProvenance(
  target: string,
  reviewerReference: string,
): Provenance {
  return {
    resourceType: 'Provenance',
    target: [{ reference: target }],
    recorded: new Date().toISOString(),
    activity: {
      coding: [
        { system: DATA_OPERATION_SYSTEM, code: 'UPDATE', display: 'revise' },
      ],
    },
    agent: [
      {
        type: {
          coding: [
            { system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'verifier' },
          ],
        },
        who: { reference: reviewerReference },
      },
    ],
  };
}

function assertNoFailedEntries(response: Bundle, expectedCount: number): void {
  const entries = response.entry ?? [];
  if (entries.length !== expectedCount) {
    throw new Error(
      `Review write failed: expected ${expectedCount} response entries, got ${entries.length}`,
    );
  }
  const failed = entries.filter((entry) => {
    const code = Number.parseInt(entry.response?.status ?? '', 10);
    return !(code >= 200 && code < 300);
  });
  if (failed.length > 0) {
    throw new Error(
      `Review write failed: ${failed
        .map((entry) => entry.response?.status ?? 'unknown')
        .join(', ')}`,
    );
  }
}
